import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Animated,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
  FlatList,
  Image,
  Pressable,
  useWindowDimensions,
  Platform,
  Linking,
  InteractionManager,
} from "react-native";
import {
  BtnSidebarClosePng,
  LightLines,
  ModalTitle,
  NotificationPng,
  RecycleBin,
} from "../../../assets";
import { useDevice } from "../../../utils/hooks/useDevice";
import { useDispatch, useSelector } from "react-redux";
import {
  selectNotifications,
  selectNotificationsLoading,
  selectUnreadCount,
} from "../../../store/userNotifications/notificationsReducer";
import {
  markReadRequest,
  markSeenRequest,
  markAllSeenRequest,
  notificationsRequest,
} from "../../../store/userNotifications/notificationsActions";
import { NotificationItem } from "../../../store/types";
import { useTranslation } from "react-i18next";
import ModalReusable from "./ModalReusable";
import { useNavigation } from "@react-navigation/native";
import RenderHTML from "react-native-render-html";

interface IProps {
  onClose: () => void;
}

type BackendNotification = any;

type UiNotification = {
  id: string;
  titleKey?: string;
  bodyKey?: string;
  valueParts: string[];
  url?: string | null;
  raw: BackendNotification;
};

function splitValue(value: any): string[] {
  if (value == null) return [];

  if (typeof value === "string") {
    return value
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);
  }

  return [];
}

function replacePercents(text: string, parts: string[]) {
  let out = text;
  let idx = 0;
  while (out.includes("%%") && idx < parts.length) {
    out = out.replace("%%", parts[idx]);
    idx += 1;
  }
  return out;
}

const navigateByNotificationUrl = (url: string, navigation: any) => {
  if (!url) return;

  const nav = navigation.getParent?.() ?? navigation;

  const roomMatch = url.match(/^\/room\/([^/]+)\/([^/]+)$/);
  if (roomMatch) {
    const [, roomSlug, roomId] = roomMatch;
    console.log("NAV STATE:", navigation.getState?.());
    console.log(
      "CAN NAVIGATE TO Room?",
      navigation.getState?.()?.routeNames?.includes?.("Room"),
    );
    nav.navigate("Room", {
      roomSlug: roomSlug.toLowerCase(),
      roomId: String(roomId),
    });
    return;
  }

  const tournamentMatch = url.match(/^\/tournament\/([^/]+)\/room\/([^/]+)$/);
  if (tournamentMatch) {
    const [, tournamentId, roomId] = tournamentMatch;
    nav.navigate("Tournament", {
      tournamentId: String(tournamentId),
      roomId: String(roomId),
    });
    return;
  }

  console.warn("Unknown notification url:", url);
};

export default function MessagesModal({ onClose }: IProps) {
  const opacity = useRef(new Animated.Value(0)).current;
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const navigation = useNavigation<any>();
  const { navigate } = navigation;

  const { isDesktop } = useDevice();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { width } = useWindowDimensions();

  const loading = useSelector(selectNotificationsLoading);
  const unread = useSelector(selectUnreadCount);

  const rawNotifications: NotificationItem[] = useSelector(selectNotifications);

  useEffect(() => {
    dispatch(notificationsRequest());
  }, [dispatch]);

  useEffect(() => {
    Animated.timing(opacity, {
      toValue: 1,
      duration: 200,
      useNativeDriver: true,
    }).start();

    return () => {
      opacity.setValue(0);
    };
  }, [opacity]);
  const ignoreRowPressRef = useRef(false);

  const uiMessages: UiNotification[] = useMemo(() => {
    if (!Array.isArray(rawNotifications)) return [];
    return rawNotifications
      .filter(Boolean)
      .map((n: any) => {
        const url = n?.url ?? n?.template?.url ?? null;

        return {
          id: String(n?.id ?? ""),

          titleKey: n?.template?.title ?? n?.title,
          bodyKey: n?.template?.message ?? n?.body,

          valueParts: splitValue(n?.valueParts),
          url,
          raw: n,
        };
      })
      .filter((x) => !!x.id);
  }, [rawNotifications]);

  const getTexts = (item: UiNotification) => {
    const titleTemplate = item.titleKey
      ? t(item.titleKey, { defaultValue: item.titleKey })
      : "";

    const bodyTemplate = item.bodyKey
      ? t(item.bodyKey, { defaultValue: item.bodyKey })
      : "";

    const title = titleTemplate.includes("%%")
      ? replacePercents(titleTemplate, item.valueParts)
      : titleTemplate;

    const body = bodyTemplate.includes("%%")
      ? replacePercents(bodyTemplate, item.valueParts)
      : bodyTemplate;

    return { title, body };
  };

  const handleClose = () => {
    const unreadNotifications = rawNotifications.filter((n) => !n.read);
    if (unreadNotifications.length > 0) {
      dispatch(markAllSeenRequest());
    }
    onClose();
  };

  return (
    <ModalReusable
      visible
      onClose={handleClose}
      titleImage={ModalTitle}
      title={t("T_SITE_NOTIFICATION_INBOX")}
      titleImageStyles={{ top: -54, width: 240, height: 90 }}
      titleTextStyles={{ width: 240, height: 90, fontSize: 18, marginTop: 4 }}
      closeIcon={BtnSidebarClosePng}
      closeButtonSize="small"
    >
      {!loading && uiMessages.length === 0 && (
        <View style={styles.emptyWrap}>
          <Text style={styles.emptyText}>No messages</Text>
        </View>
      )}

      <FlatList
        style={{
          paddingTop: 30,
          minWidth: isDesktop ? 600 : 300,
        }}
        data={uiMessages}
        keyExtractor={(item) => item.id}
        contentContainerStyle={styles.messageList}
        showsVerticalScrollIndicator={false}
        renderItem={({ item, index }) => {
          const { title, body } = getTexts(item);
          if (!title && !body) return null;

          const showDelete = Platform.OS !== "web" || hoveredIndex === index;

          return (
            <View>
              <Pressable
                onHoverIn={() => setHoveredIndex(index)}
                onHoverOut={() => setHoveredIndex(null)}
                onPressIn={() => setHoveredIndex(index)}
                onPressOut={() => setHoveredIndex(null)}
                style={[styles.messageRow, item.url ? styles.rowLink : null]}
                onPress={() => {
                  console.log(
                    "✅ ROW PRESS FIRED",
                    Date.now(),
                    item.id,
                    item.url,
                  );

                  if (ignoreRowPressRef.current) {
                    console.log("⛔ ignored because delete press");
                    return;
                  }

                  if (item.url) {
                    console.log("➡️ calling navigateByNotificationUrl");
                    navigateByNotificationUrl(item.url, navigation);
                  }

                  InteractionManager.runAfterInteractions(() => {
                    console.log("✅ closing modal");
                    onClose();
                  });
                  if (ignoreRowPressRef.current) return;

                  console.log("👉 notification pressed", {
                    id: item.id,
                    url: item.url,
                    valueParts: item.valueParts,
                  });

                  const notification = rawNotifications.find(
                    (n) => String(n.id) === String(item.id),
                  );
                  if (notification && !notification.read) {
                    dispatch(markSeenRequest({ notificationId: item.id }));
                  }

                  if (item.url) {
                    navigateByNotificationUrl(item.url, navigation);
                    InteractionManager.runAfterInteractions(() => onClose());
                  } else {
                    console.log("No URL, not closing");
                  }
                }}
              >
                <Image source={NotificationPng} style={styles.icon} />

                <View style={{ flex: 1 }}>
                  {!!title && <Text style={styles.messageTitle}>{title}</Text>}

                  {!!body && (
                    <RenderHTML
                      contentWidth={width - 140}
                      source={{ html: `<span>${body}</span>` }}
                      baseStyle={styles.htmlBase}
                      tagsStyles={{
                        b: styles.htmlBold,
                        span: styles.htmlBase,
                      }}
                    />
                  )}
                </View>

                {/* ✅ Delete button */}
                {showDelete && (
                  <Pressable
                    onPressIn={() => {
                      ignoreRowPressRef.current = true;
                    }}
                    onPress={() => {
                      dispatch(markReadRequest({ notificationId: item.id }));
                    }}
                    onPressOut={() => {
                      setTimeout(() => {
                        ignoreRowPressRef.current = false;
                      }, 0);
                    }}
                    hitSlop={10}
                  >
                    <Image source={RecycleBin} style={styles.deleteIcon} />
                  </Pressable>
                )}
              </Pressable>

              {index + 1 !== uiMessages.length && (
                <Image source={LightLines} style={styles.separator} />
              )}
            </View>
          );
        }}
      />
    </ModalReusable>
  );
}

const styles = StyleSheet.create({
  messageList: {
    padding: 10,
    marginTop: -18,
  },
  messageRow: {
    flexDirection: "row",
    alignItems: "center",
    marginVertical: 8,
  },
  rowLink: {
    opacity: 0.98,
  },
  htmlBase: {
    color: "white",
    fontSize: 14,
    opacity: 0.9,
  },
  htmlBold: {
    fontWeight: "800",
    color: "white",
  },
  icon: {
    width: 40,
    height: 40,
    marginRight: 10,
    tintColor: "white",
  },
  messageTitle: {
    color: "white",
    fontSize: 16,
    fontWeight: "800",
  },
  messageText: {
    color: "white",
    fontSize: 14,
    opacity: 0.9,
    marginTop: 2,
    flexShrink: 1,
  },
  deleteIcon: {
    width: 20,
    height: 20,
    tintColor: "red",
    marginLeft: 10,
  },
  separator: {
    position: "absolute",
    width: "100%",
    bottom: -4,
    height: 7,
  },
  emptyWrap: {
    paddingTop: 30,
    minWidth: 300,
    alignItems: "center",
    justifyContent: "center",
  },
  emptyText: {
    color: "white",
    fontSize: 16,
    opacity: 0.8,
  },
});
